import { GuidanceResponse, type IGuidanceResponse, type Task } from '@core/fhir-r4/fhir';
import {
  PreTriageGatewayError,
  type PreTriageGateway,
  type PreTriageGatewayBundle,
  type PreTriageGatewayBundleResource,
  type PreTriageGatewayResult,
} from '@core/ports/PreTriageGateway';
import { AuthenticatedBffTransport } from '@infrastructure/http/AuthenticatedBffTransport';
import { BffTransportError } from '@infrastructure/http/BffTransportError';
import {
  getDownstreamFailureReason,
  getTransientRetryStopFailureReason,
  hasCommitAmbiguousStop,
  hasTransientRetryStop,
} from '@infrastructure/http/retryOutcomeHeaders';
import { JourneyTimingResponseContext } from '@infrastructure/observability/JourneyTimingResponseContext';

interface FhirBundleEntry {
  resource?: PreTriageGatewayBundleResource;
}

interface FhirBundle extends PreTriageGatewayBundle {
  entry?: FhirBundleEntry[];
}

interface FhirGuidanceResponsePayload extends Partial<IGuidanceResponse> {
  resourceType?: 'GuidanceResponse';
  [key: string]: unknown;
}

export class BffPreTriageHttpGateway implements PreTriageGateway {
  private static readonly offlineMessage = 'Your internet connection appears to be unavailable. Reconnect to the internet, then start again from the NHS App if you still need help.';

  private static readonly sessionRequiredMessage = 'Start this journey from the NHS App to continue.';

  private static readonly requestFailedMessage = 'Unable to load the information needed to continue this journey.';

  public constructor(
    private readonly transport = new AuthenticatedBffTransport(),
    private readonly journeyTimingResponseContext = new JourneyTimingResponseContext(),
  ) {}

  public async applyTask(taskPayload: Task): Promise<PreTriageGatewayResult> {
    const response = await this.fetchResponse('/api/triage/PlanDefinition/$apply', {
      method: 'POST',
      headers: {
        Accept: 'application/fhir+json, application/json',
        'Content-Type': 'application/fhir+json',
      },
      body: JSON.stringify(taskPayload),
    });

    if (!response.ok) {
      if (hasCommitAmbiguousStop(response.headers)) {
        throw new PreTriageGatewayError(
          'commit-ambiguous-stop',
          'We could not confirm whether your last answer was submitted.',
          getDownstreamFailureReason(response.headers),
        );
      }

      if (hasTransientRetryStop(response.headers)) {
        throw new PreTriageGatewayError(
          'retry-exhausted',
          'Intelligent Navigator could not be reached right now.',
          getTransientRetryStopFailureReason(response.headers),
        );
      }

      throw new PreTriageGatewayError(
        'request-failed',
        BffPreTriageHttpGateway.requestFailedMessage,
        getDownstreamFailureReason(response.headers),
      );
    }

    const payload = await response.json() as unknown;
    const gatewayResult = this.extractGatewayResult(payload);

    if (!gatewayResult) {
      throw new PreTriageGatewayError(
        'request-failed',
        'The triage response could not be interpreted as a GuidanceResponse.',
      );
    }

    const backendJoinKey = this.journeyTimingResponseContext.captureResponseHeaders(response.headers);

    return backendJoinKey
      ? {
          ...gatewayResult,
          backendJoinKey,
        }
      : gatewayResult;
  }

  private extractGatewayResult(payload: unknown): PreTriageGatewayResult | null {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const candidate = payload as FhirGuidanceResponsePayload;
    if (candidate.resourceType === 'GuidanceResponse') {
      return {
        guidanceResponse: new GuidanceResponse(candidate),
      };
    }

    const bundle = payload as FhirBundle;
    if (bundle.resourceType !== 'Bundle' || !Array.isArray(bundle.entry)) {
      return null;
    }

    const guidanceResponsePayload = this.extractBundleGuidanceResponsePayload(bundle);
    if (!guidanceResponsePayload) {
      return null;
    }

    return {
      guidanceResponse: new GuidanceResponse(guidanceResponsePayload),
      bundle,
    };
  }

  private extractBundleGuidanceResponsePayload(bundle: FhirBundle): FhirGuidanceResponsePayload | null {
    if (!Array.isArray(bundle.entry)) {
      return null;
    }

    for (const entry of bundle.entry) {
      if (!entry?.resource || typeof entry.resource !== 'object') {
        continue;
      }

      const resource = entry.resource as FhirGuidanceResponsePayload;
      if (resource.resourceType === 'GuidanceResponse') {
        return resource;
      }
    }

    return null;
  }

  private async fetchResponse(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
    let response: Response;

    try {
      response = await this.transport.fetch(input, init);
    } catch (error) {
      if (error instanceof BffTransportError && error.code === 'offline') {
        throw new PreTriageGatewayError('offline', BffPreTriageHttpGateway.offlineMessage);
      }

      throw error;
    }

    if (response.status === 401) {
      throw new PreTriageGatewayError('session-required', BffPreTriageHttpGateway.sessionRequiredMessage);
    }

    return response;
  }

}